<script setup>
import {ref, computed, watch, reactive, onMounted} from 'vue'
import { useRoute, useRouter } from 'vue-router'
import markdownit from 'markdown-it'
import {EyeIcon, ClockIcon, UserCircleIcon, MagnifyingGlassIcon, 
        PencilIcon, FireIcon, CheckIcon} from '@heroicons/vue/24/solid'

import Header from './Header.vue'
import Tooltip from './Tooltip.vue'
import ButtonLoad from './ButtonLoad.vue'

import {useMessageStore } from '@/stores/messagestore.js'
const messageStore = useMessageStore();

import {useUserStore } from '@/stores/userstore.js'
const userStore = useUserStore();

const app_server = import.meta.env.VITE_APP_SERVER;
const router = useRouter();
const route = useRoute();
var slug;

const props = defineProps({
});

const page_size = 20;

const extra_data = reactive({
    posts: [],
    offset: 0,
    total: 0,
    search: "",
    featured: "featured",

    writer_mode: false
});

const post_data = reactive({
    title: "",
    summary: "",
});

onMounted(() => {
    extra_data.writer_mode = route.query.writer_mode != null;
    extra_data.featured = route.query.featured || "featured";
    get_posts();
});

async function get_posts(){
    var url = app_server + `/blog/`;
    const headers = {
            "Content-Type": "application/json",
    };

    if(extra_data.writer_mode){
        url += "write/";
        headers["Authorization"] = "Bearer " + localStorage.getItem("jwt");
    }
    
    const get_parameters = {
        offset: extra_data.offset,
        search: extra_data.search,
        featured: extra_data.featured,
    };
    const search_params = new URLSearchParams(get_parameters)
    
    url += "?" + search_params.toString()

    const response = await fetch(url , {
        method: "GET",
        mode: "cors",
        headers: headers,
    });
    
    const blog_data = await response.json();
    if(blog_data.status == "success"){
        extra_data.posts = blog_data.posts;
        extra_data.total = blog_data.total;
    }    

    console.log(blog_data);
}

async function next(delta){
    var new_offset = extra_data.offset + delta;

    new_offset = Math.min(new_offset, extra_data.total - page_size);
    new_offset = Math.max(0, new_offset);

    extra_data.offset = new_offset;

    await get_posts();
}

async function create_post(){
    const response = await fetch(app_server + `/blog/write/`, {
        method: "POST",
        mode: "cors",
        headers:{
            "Content-Type": "application/json",
            "Authorization": "Bearer " + localStorage.getItem("jwt"),
        },
        body: JSON.stringify(
            post_data
        ),
    });
    const data = await response.json();

    if(data.status == "error"){
        messageStore.alertUser("Error creating post", 
            data.message || "Could not create blog post, please contact the administrators" 
        );
        return;
    }
    router.push({name: "blog-write", params: {"slug": data.post.slug}});    

}

function humanize(n){
    if(n > 1000000){
        return `${(n / 1000000).toFixed(1)}M`
    }
    else if(n > 1000){
        return `${(n / 1000).toFixed(1)}k`
    }
    else{
        return n;
    }
}

async function set_mode(mode){
    const query_params = Object.assign({}, route.query);
    if(mode == "writer"){
        extra_data.writer_mode = true;
        query_params.writer_mode = "writer";
    }
    else if(mode == "reader"){
        extra_data.writer_mode = false;
        query_params.writer_mode = null;
    }
    console.log(route.query);
    router.replace({name: 'blog-list', query: query_params});

    get_posts();
}

async function set_flag(flag){
    const query_params = Object.assign({}, route.query);
    if(flag == "featured"){
        extra_data.featured = "featured";
        query_params.featured = "featured";
    }
    else{
        extra_data.featured = "";
        query_params.featured = null;
    }
    router.replace({name: 'blog-list', query: query_params});
    get_posts();
}

</script>

<template>
    <Header />
    <div class="container mx-auto flex flex-col items-center gap-y-8">
    <h1 class="text-4xl font-semibold text-center mt-8 tracking-wide flex items-center gap-x-8">
        <span>OpenGammon Blog</span>
        <div v-if="userStore && userStore.authenticated">
        <Tooltip v-if="!extra_data.writer_mode"
            text="Enter the writer mode."
        >
            <PencilIcon class="size-8 cursor-pointer hover:text-accent-3-color" 
                @click="set_mode('writer')"
            />
        </Tooltip>
        <Tooltip v-if="extra_data.writer_mode"
            text="Enter the reader mode."
        >
            <EyeIcon class="size-8 cursor-pointer hover:text-accent-3-color" 
                @click="set_mode('reader')"
            />
        </Tooltip>
        </div>
    </h1>

    <div class="flex items-center gap-x-1">
        <input 
            type="text" class="h-10" v-model="extra_data.search"
        />
        <ButtonLoad
            :fn="get_posts"
            class="cursor-pointer size-8"
        >
            <MagnifyingGlassIcon 
                class="size-8 hover:text-accent-3-color" 
            />
        </ButtonLoad>
    </div>
    <div class="flex items-center gap-x-2">
        <ButtonLoad v-for="flag in [['Featured', 'featured'], ['All', '']]"
             class="w-32 text-center btn btn-blue"
             :fn="() => set_flag(flag[1])"
             :disabled="extra_data.featured == flag[1]"
        >
            {{ flag[0] }}
        </ButtonLoad>
    </div>
    <div class="flex flex-col w-full max-w-prose"
         v-if="extra_data.writer_mode"
    >
        <button class="btn btn-blue text-xl h-16 min-w-64"
                @click="extra_data.view_create_form = true"
                :disabled="extra_data.view_create_form"

        >
            Create Blog Post +
        </button>
        <div 
            v-if="extra_data.view_create_form"
            class="flex flex-col my-8 p-6 rounded-lg box bg-main-2-color"
        >
            <div>
                <label>Title</label>
                <input type="text" data-lesson
                       class="text-xl w-full" 
                       placeholder="A catchy title for your blog post"
                       v-model="post_data.title" 
                       required
                />
            </div>
            <div>
                <label>Summary</label>
                <textarea  data-lesson class="w-full" 
                    v-model="post_data.summary" 
                    placeholder="A short summary of the blog post that is shown in the Blog overview." 
                    required
                />
            </div>
            <ButtonLoad class="btn btn-blue h-12 my-4"
                :fn="create_post"
                :disabled="!post_data.summary || !post_data.title"
            >
                Create
            </ButtonLoad>
            <button class="btn btn-danger"
                @click="extra_data.view_create_form = false"
            >
                Cancel
            </button>
        </div>
    </div>
    <div v-if="extra_data.posts.length == 0"
         class="flex items-center text-xl my-12 font-light text-center"
    >
        We could not find any blog posts... 
    </div>
    <div class="grid grid-cols-1 md:grid-cols-2 w-full gap-8">
        <div class="flex box shadow rounded-lg bg-main-2-color py-4 px-6 group relative transition-all" 
            v-for="post in extra_data.posts"
        >
            <div class="grow">
                <router-link :to="{name: 'blog-article', params:{slug: post.slug}}">
                    <h2 class="text-3xl text-center tracking-wide font-medium 
                                mb-4 group-hover:text-accent-3-color"
                    >
                        {{ post.title }}
                    </h2>
                </router-link>
                <div class="flex gap-x-4 mt-3 opacity-60 group-hover:opacity-100">
                    <div class="flex gap-x-1 items-center">
                        <Tooltip text="This blog post is featured!" v-if="post.flag == 'featured'">
                            <FireIcon class="size-4 opacity-100 text-accent-3-color" />
                        </Tooltip>
                        <UserCircleIcon class="w-4 h-4" />
                        <span class="text-right">{{ post.author.username }}</span>
                    </div>
                    <div class="flex gap-x-1 items-center">
                        <ClockIcon class="w-4 h-4" />
                        <span>{{ new Date(post.update_time*1000).toDateString() }}</span>
                    </div>
                    <div v-if="post.views" 
                        class="flex gap-x-1 items-center">
                        <EyeIcon class="w-4 h-4" />
                        <span>{{ humanize(post.views) }}</span>
                    </div>
                    <div class="flex gap-x-1 items-center justify-self-end">
                        <span>
                            {{ post.status }}
                        </span>
                    </div>
                </div>
                <div class="mt-4 text-lg font-thin overflow-y-hidden h-24 relative">
                    <div class="absolute bottom-0 w-full h-10 bg-gradient-to-t from-main-2-color" />
                    {{ post.summary }}
                </div>
            </div>
            <div class="absolute w-full flex justify-center bottom-4 opacity-0 group-hover:opacity-100 transition-opacity delay-500">
                <router-link 
                    v-if="!extra_data.writer_mode"
                    :to="{name: 'blog-article', params:{slug: post.slug}}"
                >
                    <button class="btn btn-blue h-10 w-32">Read More</button>
                </router-link>
                <router-link 
                    v-else
                    :to="{name: 'blog-write', params:{slug: post.slug}}"
                >
                    <button class="btn btn-blue h-10 w-32">Edit</button>
                </router-link>
            </div>
        </div>
    </div>
    <div class="flex justify-between w-full px-2 mb-8">
        <ButtonLoad 
            class="btn btn-blue"
            :fn="() => next(-page_size)"
            :disabled="extra_data.offset <= 0"
        >
            Previous
        </ButtonLoad>
        <span>{{ extra_data.total }} Articles to read</span>
        <ButtonLoad 
            class="btn btn-blue"
            :fn="() => next(page_size)"
            :disabled="extra_data.offset + page_size >= extra_data.total"
        >
            Next
        </ButtonLoad>
    </div>
    </div>
</template>

<style scoped>
</style>

